import { useEffect, useRef, useState } from 'react';

export default function Contact() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="contact" ref={sectionRef} className="py-32 px-6 relative">
      <div className="max-w-7xl mx-auto">
        {/* Main Contact Card */}
        <div className={`relative transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Glow effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-amber-500 to-amber-600 rounded-3xl blur-3xl opacity-20"></div>
          
          <div className="relative bg-gradient-to-br from-gray-900 via-gray-950 to-gray-900 rounded-3xl border border-gray-800 overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Left - Contact Info */}
              <div className="p-12 lg:p-16 space-y-8">
                <div>
                  <div className="inline-block px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full mb-6">
                    <span className="text-xs uppercase tracking-[0.2em] text-amber-400 font-medium">
                      Get In Touch
                    </span>
                  </div>
                  <h2 className="text-4xl lg:text-5xl font-bold mb-4 tracking-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    Visit Our Studio
                  </h2>
                  <p className="text-gray-400 text-lg">
                    Experience personalized service and expert craftsmanship
                  </p>
                </div>

                {/* Contact Details */}
                <div className="space-y-6">
                  {/* Address */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center shadow-lg shadow-amber-500/20">
                        <i className="ri-map-pin-line text-xl text-black"></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Address</h3>
                      <p className="text-gray-400">
                        76/5A Shanthi Road<br />
                        Hendala, Wattala<br />
                        Sri Lanka
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center shadow-lg shadow-amber-500/20">
                        <i className="ri-phone-line text-xl text-black"></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Phone / WhatsApp</h3>
                      <a href="tel:+94771107594" className="text-amber-400 hover:text-amber-300 transition-colors cursor-pointer">
                        077 110 7594
                      </a>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center shadow-lg shadow-amber-500/20">
                        <i className="ri-time-line text-xl text-black"></i>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Opening Hours</h3>
                      <p className="text-gray-400">
                        Monday – Saturday: 9 AM – 8 PM<br />
                        Sunday: 9 AM – 2 PM
                      </p>
                    </div>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <a
                    href="tel:+94771107594"
                    className="flex-1 px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold rounded-full transition-all shadow-xl shadow-amber-500/30 hover:shadow-amber-500/50 text-center whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Call Now
                  </a>
                  <a
                    href="https://wa.me/94771107594"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 px-8 py-4 bg-gray-800 hover:bg-gray-700 text-white font-semibold rounded-full transition-all text-center whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line mr-2"></i>
                    WhatsApp
                  </a>
                </div>

                <a
                  href="https://www.google.com/maps/search/?api=1&query=76%2F5A+Shanthi+Road+Hendala+Wattala+Sri+Lanka"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-8 py-4 bg-gray-900/50 border border-gray-700 hover:border-amber-500 text-white font-semibold rounded-full transition-all text-center whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-map-line mr-2"></i>
                  Get Directions
                </a>
              </div>

              {/* Right - Map */}
              <div className="relative min-h-[400px] lg:min-h-full bg-gray-950">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3960.0!2d79.89!3d6.99!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNsKwNTknMjQuMCJOIDc5wrA1MyczNi4wIkU!5e0!3m2!1sen!2slk!4v1234567890"
                  className="absolute inset-0 w-full h-full border-0 grayscale hover:grayscale-0 transition-all duration-500"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Rinard Tailors Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-20 pt-12 border-t border-gray-800">
        <div className="max-w-7xl mx-auto text-center space-y-4">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center shadow-lg shadow-amber-500/20">
              <span className="text-black font-bold text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>RT</span>
            </div>
            <span className="text-xl font-semibold tracking-wide" style={{ fontFamily: 'Montserrat, sans-serif' }}>Rinard Tailors</span>
          </div>
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Rinard Tailors – Specialist in Gents. All rights reserved.
          </p>
          <div className="flex items-center justify-center gap-2 text-gray-600 text-xs">
            <span>Crafted with precision</span>
            <span>·</span>
            <a 
              href="https://readdy.ai/?origin=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-amber-400 transition-colors cursor-pointer"
            >
              Website Builder
            </a>
          </div>
        </div>
      </footer>
    </section>
  );
}
